import { useRef } from 'react'
import Image from 'next/image'
import {
  Stack,
  VStack,
  AspectRatio,
  Text,
  // Image,
  Button,
  Link,
  Box,
} from '@chakra-ui/react'
import { darken } from '@chakra-ui/theme-tools'

import ParallaxNumber from '../../atoms/ParallaxNumber'
import CheckList from '@/components/CheckList'

import { Product as ProductType } from '../../../types'

interface Props extends ProductType {
  index: number
}

function Product({
  id,
  primaryColor,
  secondaryColor,
  logo,
  image,
  paragraph,
  href,
  checkList,
  logoWidth,
  logoHeight,
  imageAspectRatio,
  index,
}: Props): React.ReactElement {
  const isEven = index % 2 === 0

  const containerRef = useRef(null)

  return (
    <Box
      id={id}
      ref={containerRef}
      sx={{
        backgroundColor: isEven ? '#fafafa' : 'white',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          maxWidth: '1900px',
          mx: 'auto',
        }}
      >
        <ParallaxNumber
          index={index}
          containerRef={containerRef}
          primaryColor={primaryColor}
        />
        <Text
          sx={{
            display: {
              base: 'block',
              'lg+': 'none',
            },
            position: 'absolute',
            top: 0,
            w: 'fit-content',
            fontSize: {
              base: '180px',
              md: '240px',
            },
            color: '#EEEEEE',
            transform: 'translate3d(-50%, -10%, 0)',
            left: '50%',
            right: '50%',
          }}
        >
          {index + 1}
        </Text>
        <Stack
          spacing={{
            base: 10,
            'lg+': 0,
          }}
          direction={{
            base: 'column',
            'lg+': isEven ? 'row-reverse' : 'row',
          }}
          sx={{
            position: 'relative',
            zIndex: 2,
            w: '100%',
            py: {
              base: '60px',
              md: '100px',
            },
            px: {
              base: '0',
              md: '100px',
              'lg+': '0',
            },
          }}
        >
          <AspectRatio
            ratio={logoWidth / logoHeight}
            sx={{
              display: {
                base: 'block',
                'lg+': 'none',
              },
              w: `${logoWidth}px`,
              maxW: '80%',
              alignSelf: {
                base: 'center',
                'lg+': 'flex-start',
              },
            }}
          >
            <Image src={logo} layout="fill" objectFit="contain" />
          </AspectRatio>
          <Box
            sx={{
              w: {
                base: '100%',
                'lg+': '50%',
              },
              px: {
                base: 4,
                'lg+': 8,
              },
              flex: 1,
            }}
          >
            <AspectRatio
              sx={{
                w: {
                  base: '100%',
                  'lg+': '80%',
                },
                maxW: {
                  base: 'unset',
                  'lg+': '720px',
                },
                p: 0,
                marginRight: isEven ? 'auto' : '0',
                marginLeft: isEven ? '0' : 'auto',
              }}
              ratio={imageAspectRatio}
            >
              <Image src={image} layout="fill" objectFit="contain" />
            </AspectRatio>
          </Box>
          <Box
            sx={{
              w: {
                base: '100%',
                'lg+': '50%',
              },
              px: {
                base: 8,
                'lg+': 4,
              },
              flex: 1,
            }}
          >
            <VStack
              spacing={{
                base: 0,
                'lg+': 10,
              }}
              sx={{
                w: 'fit-content',
                alignItems: 'stretch',
                marginLeft: isEven ? 'auto' : '0',
                marginRight: isEven ? '0' : 'auto',
              }}
            >
              <AspectRatio
                ratio={logoWidth / logoHeight}
                sx={{
                  display: {
                    base: 'none',
                    'lg+': 'block',
                  },
                  mt: 3,
                  w: `${logoWidth}px`,
                  alignSelf: {
                    base: 'center',
                    'lg+': 'flex-start',
                  },
                }}
              >
                <Image src={logo} layout="fill" objectFit="contain" />
              </AspectRatio>
              <VStack
                spacing="4"
                sx={{
                  alignItems: 'stretch',
                  maxWidth: {
                    base: 'unset',
                    'lg+': '541px',
                  },
                  width: '100%',
                }}
              >
                <Text
                  sx={{
                    fontSize: {
                      base: 'lg',
                      md: 'xl',
                    },
                    fontWeight: 'light',
                    '& > strong': {
                      color: primaryColor,
                      fontWeight: 'medium',
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: paragraph,
                  }}
                ></Text>
                <CheckList
                  labels={checkList}
                  fillColor={secondaryColor}
                  strokeColor={primaryColor}
                />
                <Link
                  isExternal
                  href={href}
                  sx={{
                    h: {
                      base: 12,
                      sm: 14,
                      'lg+': 12,
                    },
                    alignSelf: {
                      base: 'center',
                      'lg+': 'flex-start',
                    },
                    pt: 6,
                    boxSizing: 'content-box',
                    borderRadius: '9999px',
                    textDecoration: 'none !important',
                  }}
                >
                  <Button
                    as="div"
                    size="md"
                    sx={{
                      height: '100%',
                      px: 9,
                      color: 'white',
                      borderRadius: '9999px',
                      backgroundColor: primaryColor,
                      '&:hover': {
                        backgroundColor: darken(primaryColor, 10),
                      },
                    }}
                  >
                    ดูข้อมูลเพิ่มเติม
                  </Button>
                </Link>
              </VStack>
            </VStack>
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}

export default Product
